import type { Sequence } from '@/constants/crm'

import { useState } from 'react'
import Avatar from '@/components/ui/avatar'
import Button from '@/components/ui/button'
import Tag from '@/components/ui/tag'
import FilterMenu from '@/components/generals/filter-menu'
import { EmptyState, ViewToolbar } from '@/components/generals/view-parts'
import { nextStatusAction } from '@/components/sequences/sequence-detail'
import { ownerByName } from '@/constants/companies'
import { SEQUENCE_STATUSES, sequenceStatusMeta } from '@/constants/crm'
import { apiRequest } from '@/lib/client-api'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

const ALL = 'all'

const STATUS_OPTIONS = [
    { value: ALL, label: "All" },
    ...SEQUENCE_STATUSES.map(status => ({ value: status.value, label: status.value }))
]

export default function SequencesView() {
    const status = useCrmStore(state => state.status)
    const sequences = useCrmStore(state => state.sequences)
    const upsertSequence = useCrmStore(state => state.upsertSequence)
    const openDialog = useCrmStore(state => state.openDialog)
    const openSequence = useCrmStore(state => state.openSequence)
    const showNotice = useCrmStore(state => state.showNotice)
    const [statusFilter, setStatusFilter] = useState(ALL)
    const [busyId, setBusyId] = useState<string | null>(null)

    const visible = sequences.filter(sequence => statusFilter === ALL || sequence.status === statusFilter)

    async function changeStatus(sequence: Sequence) {
        const action = nextStatusAction(sequence.status)

        setBusyId(sequence.id)

        const result = await apiRequest<{ sequence: Sequence }>(`/api/sequences/${sequence.id}`, 'PATCH', { status: action.status })

        setBusyId(null)

        if (!result.ok) {
            showNotice('error', result.message)

            return
        }

        upsertSequence(result.data.sequence)
        showNotice('success', `${sequence.name} ${action.status === 'Paused' ? 'paused' : 'is active'}.`)
    }

    return (
        <>
            <ViewToolbar>
                <FilterMenu label="Status" value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
                <Button variant='primary' size='sm' onClick={() => openDialog('sequence')}>
                    <PlusIcon aria-hidden className='size-3' />
                    New Sequence
                </Button>
            </ViewToolbar>

            <div className='border-border flex min-h-0 flex-1 flex-col overflow-y-auto border-t'>
                <p className='caption-style text-subtle border-border border-b px-4 py-3 leading-[1.4]'>
                    Sequences plan the outreach steps your team works through. They track steps and results here and do not send emails on their own.
                </p>

                {visible.length === 0 ? (
                    <EmptyState
                        title={status === 'loading' ? "Loading sequences…" : "No sequences here"}
                        description={status === 'loading' ? undefined : "Create a sequence to plan the touches for a campaign or segment."}
                    />
                ) : (
                    <ul className='grid grid-cols-1 gap-3 p-4 md:grid-cols-2 2xl:grid-cols-3'>
                        {visible.map(sequence => {
                            const meta = sequenceStatusMeta(sequence.status)
                            const owner = ownerByName(sequence.owner)
                            const action = nextStatusAction(sequence.status)

                            return (
                                <li key={sequence.id}>
                                    <article className='bg-card flex h-full flex-col gap-4 rounded-xl p-4 shadow-[0px_4px_4px_0px_rgba(15,15,15,0.24),0px_0px_0px_1px_#0e0e0e,inset_0px_1px_0px_0px_rgba(255,255,255,0.06),inset_0px_0px_0px_1px_rgba(255,255,255,0.06)]'>
                                        <div className='flex items-start justify-between gap-3'>
                                            <div className='flex min-w-0 flex-col gap-2'>
                                                <h2 className='truncate'>
                                                    <button
                                                        type='button'
                                                        onClick={() => openSequence(sequence.id)}
                                                        className='hover:text-soft cursor-pointer text-left transition-colors duration-150'
                                                    >
                                                        <span data-mdlfy-cms={`email_sequences:${sequence.id}:name`}>
                                                            {sequence.name}
                                                        </span>
                                                    </button>
                                                </h2>
                                                <p className='text-soft line-clamp-2 leading-[1.35]' data-mdlfy-cms={`email_sequences:${sequence.id}:description`}>
                                                    {sequence.description}
                                                </p>
                                            </div>
                                            <Tag tone={meta.tone} size='sm' className='text-[12px]'>
                                                {meta.value}
                                            </Tag>
                                        </div>

                                        <dl className='grid grid-cols-4 gap-2'>
                                            {[
                                                { label: "Steps", value: String(sequence.steps.length) },
                                                { label: "Enrolled", value: String(sequence.enrolled) },
                                                { label: "Opened", value: `${sequence.openRate}%` },
                                                { label: "Replied", value: `${sequence.replyRate}%` }
                                            ].map(stat => (
                                                <div key={stat.label} className='border-line-strong flex flex-col gap-2 rounded-lg border p-2.5'>
                                                    <dt className='caption-style text-subtle'>{stat.label}</dt>
                                                    <dd className='lead-style tabular-nums'>{stat.value}</dd>
                                                </div>
                                            ))}
                                        </dl>

                                        <div className='mt-auto flex items-center justify-between gap-2'>
                                            <span className='caption-style text-soft flex min-w-0 items-center gap-1.5'>
                                                <Avatar src={owner.avatar} alt='' />
                                                <span className='truncate' data-mdlfy-cms={`email_sequences:${sequence.id}:owner`}>
                                                    {sequence.owner}
                                                </span>
                                            </span>
                                            <span className='flex shrink-0 items-center gap-1'>
                                                <Button variant='ghost' size='sm' disabled={busyId === sequence.id} onClick={() => changeStatus(sequence)}>
                                                    {action.label}
                                                </Button>
                                                <Button variant='secondary' size='sm' onClick={() => openSequence(sequence.id)}>
                                                    Open
                                                </Button>
                                            </span>
                                        </div>
                                    </article>
                                </li>
                            )
                        })}
                    </ul>
                )}
            </div>
        </>
    )
}
